import { fechaArgentina, formateaMoneda, nombreDeComprobante } from './dominio';
import { useResumen } from './consultas';
import { mensajeDeConsulta } from './mensajes';

/**
 * Totales del período, por tipo de comprobante (KAN-146).
 *
 * # LOS TOTALES LOS CALCULA LA BASE, NO EL NAVEGADOR
 *
 * Este panel **no suma las filas que se ven**. Dos razones, y las dos son
 * suficientes por sí solas:
 *
 * - El listado viene paginado: sumar lo visible daría el total de la página, no
 *   el del período.
 * - Sumar importes con `number` en JavaScript rompe centavos. Los importes son
 *   `BigDecimal` de escala 2 en el servidor y ahí se agregan; acá solo se
 *   formatean.
 *
 * # LAS NOTAS DE CRÉDITO SUMAN EN POSITIVO
 *
 * Es una decisión del contrato, no un olvido: esto cuenta **comprobantes
 * emitidos**, y una NC es un comprobante emitido. Restarlas para obtener la
 * base imponible del período es un cálculo de libro de ventas (KAN-87) con
 * criterios fiscales propios. Cada tipo viene en su renglón, marcado, para que
 * quien lo necesite lo haga con las reglas correctas en vez de heredar una
 * resta escondida — y el cartel lo dice en la pantalla, porque un total que
 * parece la facturación del mes y no lo es se copia a un papel sin preguntar.
 *
 * Sin período completo no hay resumen: `desde` y `hasta` son obligatorios en el
 * contrato. Se dice explícitamente, en vez de mostrar un panel vacío que
 * parecería decir "no facturaste nada".
 */
export function ResumenDelPeriodo({
  tenantId,
  desde,
  hasta,
}: {
  tenantId: number | null;
  desde: string;
  hasta: string;
}) {
  const resumen = useResumen(tenantId, desde, hasta);

  if (tenantId === null) {
    return null;
  }

  if (desde === '' || hasta === '') {
    return (
      <section aria-labelledby="resumen-titulo">
        <h2 id="resumen-titulo">Resumen del período</h2>
        <p className="nota">
          Completá "Desde" y "Hasta" para ver los totales del período. Un resumen sin período no
          significa nada.
        </p>
      </section>
    );
  }

  return (
    <section aria-labelledby="resumen-titulo">
      <h2 id="resumen-titulo">
        Resumen del {fechaArgentina(desde)} al {fechaArgentina(hasta)}
      </h2>

      {resumen.isPending ? <p role="status">Calculando el resumen…</p> : null}

      {resumen.isError ? (
        <p role="alert" className="error">
          {mensajeDeConsulta(resumen.error, 'resumen')}
        </p>
      ) : null}

      {resumen.data ? (
        resumen.data.porTipo.length === 0 ? (
          <p>No hubo movimientos en ese período.</p>
        ) : (
          <table>
            <caption className="nota">
              Las notas de crédito suman en positivo: acá se cuentan comprobantes emitidos. Para la
              base imponible del período hay que restarlas con las reglas del libro de ventas.
            </caption>
            <thead>
              <tr>
                <th scope="col">Tipo</th>
                <th scope="col">Cantidad</th>
                <th scope="col">Neto</th>
                <th scope="col">IVA</th>
                <th scope="col">Total</th>
              </tr>
            </thead>
            <tbody>
              {resumen.data.porTipo.map((renglon) => (
                <tr key={renglon.cbteTipo}>
                  <td>{nombreDeComprobante(renglon.cbteTipo, renglon.letra)}</td>
                  <td className="importe">{renglon.cantidad}</td>
                  <td className="importe">{formateaMoneda(renglon.neto)}</td>
                  <td className="importe">{formateaMoneda(renglon.iva)}</td>
                  <td className="importe">{formateaMoneda(renglon.total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row">Total emitido</th>
                <td className="importe">{resumen.data.totales.cantidad}</td>
                <td className="importe">{formateaMoneda(resumen.data.totales.neto)}</td>
                <td className="importe">{formateaMoneda(resumen.data.totales.iva)}</td>
                <td className="importe">
                  <strong>{formateaMoneda(resumen.data.totales.total)}</strong>
                </td>
              </tr>
            </tfoot>
          </table>
        )
      ) : null}
    </section>
  );
}
